import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import useRoles from "../../../../features/Roles/hooks/useRoles";
import { useEffect, useState } from "react";
import { 
  Button, 
  Center, 
  Flex, 
  Heading, 
  VStack, 
  Box,
  Text,
  Spinner,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Input,
  Select,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { AdminSeasonChallengeTask, OlaggModule, PermissionEnum } from "@olagg/db-types";
import Pagination from "@olagg/ui-kit/Pagination";
import useSeasonChallengeTasks from "./hooks/useSeasonChallengeTasks";
import moment from "moment";

const Actions = ({ task, seasonId, challengeId }: { task: AdminSeasonChallengeTask, seasonId: string, challengeId: string }) => {
  const { missingPermission } = useRoles();
  const { deleteSeasonChallengeTask } = useSeasonChallengeTasks();
  const navigate = useNavigate();
  
  const handleViewSeasonChallengeTask = () => navigate(`/seasons/${seasonId}/challenges/${challengeId}/tasks/${task.id}`);
  
  const handleRemoveSeasonChallengeTask = () => {
    if (window.confirm('¿Estás seguro que deseas eliminar esta tarea?')) {
      deleteSeasonChallengeTask(task.id.toString()).then(() => {
        console.log("Tarea eliminada")
        window.location.reload();
      }).catch(console.log)
    }
  }

  const canEdit = !missingPermission(OlaggModule.Season, PermissionEnum.EDIT);
  const canDelete = !missingPermission(OlaggModule.Season, PermissionEnum.DELETE);

  return (
    <Box display="flex" gap="10px" flexWrap="wrap">
      <Button 
        isDisabled={!canEdit} 
        colorScheme="green"
        size="sm"
        onClick={handleViewSeasonChallengeTask}
      >
        Ver
      </Button>
      <Button 
        isDisabled={!canDelete} 
        colorScheme="red"
        size="sm"
        onClick={handleRemoveSeasonChallengeTask}
      >
        Eliminar
      </Button>
    </Box>
  )
}

const SeasonChallengeTasks = () => {
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const { season_id: seasonId, challenge_id: challengeId } = useParams<{ season_id: string, challenge_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getSeasonChallengeTasks, loading } = useSeasonChallengeTasks();
  const [seasonChallengeTasks, setSeasonChallengeTasks] = useState<AdminSeasonChallengeTask[]>([]);
  const [count, setCount] = useState(0);
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const [debouncedActionType, setDebouncedActionType] = useState('');
  
  const [filter, setFilter] = useState({
    title: searchParams.get('title') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
    action_type: searchParams.get('action_type') || '',
  })
  const { page, limit, title, action_type } = filter

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (name === 'page' || name === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue })

    if (value) searchParams.set(name, value)
    else searchParams.delete(name)
    setSearchParams(searchParams)
  }

  const onClearFilters = () => {
    setFilter({
      title: '',
      page: 1,
      limit: 10,
      action_type: '',
    });
    setSearchParams(new URLSearchParams());
  }

  const fetchSeasonChallengeTasks = async () => {
    if (!challengeId) return;
    
    try {
      const response = await getSeasonChallengeTasks({ 
        limit, 
        page, 
        title: debouncedTitle || undefined,
        action_type: (debouncedActionType as any) || undefined,
        game_season_challenge_id: Number(challengeId)
      });
      setSeasonChallengeTasks(response.gameSeasonChallengeTasks);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  // Debounce title filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTitle(title)
    }, 500)

    return () => clearTimeout(timer)
  }, [title])

  // Debounce action type filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedActionType(action_type)
    }, 500)

    return () => clearTimeout(timer)
  }, [action_type])

  useEffect(() => {
    fetchSeasonChallengeTasks();
  }, [page, limit, debouncedTitle, debouncedActionType, challengeId])

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlTitle = searchParams.get('title');
    const urlActionType = searchParams.get('action_type');

    if (urlPage || urlLimit || urlTitle || urlActionType) {
      setFilter({
        title: urlTitle || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 10),
        action_type: urlActionType || '',
      });
    }
  }, []);

  const getTargetInfo = (task: AdminSeasonChallengeTask) => {
    {task?.targetInfo?.title || task?.actionParams?.days || task?.actionParams?.minAmount || 'Sin objetivo especifico'}
    if (task?.targetInfo?.title) {
      return task?.targetInfo?.title?.es ? task?.targetInfo?.title?.es : task?.targetInfo?.title;
    } else if (task?.actionParams?.days) {
      return `Días: ${task?.actionParams?.days}`;
    } else if (task?.actionParams?.minAmount) {
      return `Cantidad Mínima: ${task?.actionParams?.minAmount}`;
    } else if (task?.actionParams?.articleId) {
      return `ID del Artículo: ${task?.actionParams?.articleId}`;
    }
    return 'Sin objetivo específico';
  }

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Tareas del Desafío
        </Heading>

        <Flex w='full' alignItems='center' gap='5'>
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(`/seasons/${seasonId}/challenges/${challengeId}/tasks/crear`)}
            isDisabled={missingPermission(OlaggModule.Season, PermissionEnum.CREATE)}
          >
            Agregar Tarea
          </Button>
        </Flex>

        {/* Inline filter */}
        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Título
            </Text>
            <Input
              placeholder="Buscar por título..."
              value={title}
              onChange={(e) => onFilter({ name: 'title', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            />
          </Box>

          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Tipo de Acción
            </Text>
            <Select
              value={action_type}
              onChange={(e) => onFilter({ name: 'action_type', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            >
              <option value="">Todos los tipos</option>
              <option value="quest">Quest</option>
              <option value="tournament_participation">Participación en Torneo</option>
              <option value="tournament_win">Victoria en Torneo</option>
              <option value="survey">Encuesta</option>
              <option value="treasure_play">Jugar Tesoro</option>
              <option value="treasure_win">Ganar Tesoro</option>
              <option value="streak">Racha</option>
              <option value="news_vote">Voto de Noticias</option>
              <option value="game_rating">Rating de Juego</option>
              <option value="referral">Referido</option>
              <option value="badge_claim">Reclamar Badge</option>
              <option value="shop_purchase">Compra en Tienda</option>
            </Select>
          </Box>

          <Box>
            <Text color="transparent" fontSize="sm" mb={1}>
              .
            </Text>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          seasonChallengeTasks?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                        <Th py={'20px'} color="white">Título</Th>
                        <Th py={'20px'} color="white">Tipo de Acción</Th>
                        <Th py={'20px'} color="white">Objetivo</Th>
                        <Th py={'20px'} color="white">Fecha Creación</Th>
                        <Th py={'20px'} color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {seasonChallengeTasks?.map((task, index) => (
                        <Tr key={index} color="white">
                          <Td py={'20px'}>{task?.title}</Td>
                          <Td py={'20px'}>
                            <Badge colorScheme="blue">
                              {task.actionType?.replace(/_/g, ' ').toUpperCase()}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>
                            {getTargetInfo(task)}
                          </Td>
                          <Td py={'20px'} color="gray.400" fontSize="sm">
                            {moment(task.createdAt).format('DD/MM/YYYY HH:mm')}
                          </Td>
                          <Td py={'20px'}>
                            <Actions task={task} seasonId={seasonId!} challengeId={challengeId!} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay tareas cargadas</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />
      </Box>
    </Center>
  )
}

export default SeasonChallengeTasks;